import { emailValidation, DIV, fillInBlankUniqueValidation, elementMutuallyDisallowsOtherOptions, chainValidations } from '../utils';
////// BASIC QUESTIONS
const fromWhich = (x) => {
    const page = 'basic-questions';
    const whichLangs = (x[`${page}${DIV}which-languages`] || []).filter((x) => x !== somethingElse);
    const customLangs = (x[`${page}${DIV}which-other-languages`] || []).filter((x) => x.length);
    return [...whichLangs, ...customLangs].filter((x) => x !== notFamiliar).sort();
};
const fromWhichCondition = (x) => fromWhich(x).length > 0;
const notFamiliar = `I'm not familiar with such a language`;
const somethingElse = 'Another not listed here';
const whichLanguages = {
    id: 'which-languages',
    type: 'checkbox-question',
    label: 'Which of the following (JSON-based) languages have you heard of?',
    options: [`MongoDB`, 'Tracery', 'Vega', 'Vega-Lite', somethingElse, notFamiliar],
    requireResponse: true,
    customValidation: chainValidations([
        (responses, key) => (!(responses[key] || []).length ? 'Must select at least one option' : null),
        elementMutuallyDisallowsOtherOptions(notFamiliar)
    ])
};
const whichLanguageNotice = {
    id: 'which-languages-notice',
    type: 'text-block',
    label: '### <span style="color:salmon">Note</span>',
    text: 'An at least passing familiarity with a JSON-DSL is required for participation in this survey.',
    condition: (x) => {
        const targ = x[`basic-questions${DIV}which-languages`];
        return !!(Array.isArray(targ) ? targ : []).find((x) => x === notFamiliar);
    }
};
const whichOtherLanguages = {
    type: 'fill-in-the-blank-list',
    label: 'You indicated that you are familiar with another JSON-based language. Which ones are you familiar with?',
    id: 'which-other-languages',
    requireResponse: true,
    condition: (responses) => {
        const target = responses[`basic-questions${DIV}which-languages`];
        return Array.isArray(target) && target.includes(somethingElse);
    },
    customValidation: chainValidations([
        (resp, key) => (!(resp[key] || []).length ? 'Requires response' : null),
        fillInBlankUniqueValidation
    ])
};
// https://mwcc.edu/wp-content/uploads/2020/09/Likert-Scale-Response-Options_MWCC.pdf
const simpleMatrixQuestion = (label, columns, id) => ({
    type: 'matrix-question',
    id,
    label,
    rows: fromWhich,
    columns,
    requireResponse: true,
    condition: fromWhichCondition
});
// const familiarity5 = [
//   '(1) Not at all familiar',
//   '(2) Slightly familiar',
//   '(3) Somewhat familiar',
//   '(4) Moderately familiar',
//   '(5) Extremely familiar '
// ];
const familiarity7 = [
    '(1) <br/> Not at all familiar',
    '(2)',
    '(3)',
    '(4) <br/> Somewhat familiar',
    '(5)',
    '(6)',
    '(7) <br/> Extremely familiar ',
    'Not Sure'
];
const langFamiliarity = simpleMatrixQuestion('How familiar are you with each of these languages?', familiarity7, 'language-familiarity');
// 'Multiple times per month',
const langFreq = simpleMatrixQuestion('How frequently do you use each of these languages?', [
    'Not Sure',
    'Everyday',
    'Most days',
    'Once a week',
    'Once a month',
    'Once a quarter',
    'More rarely',
    'Never'
].reverse(), 'language-frequency');
const agreement = [
    '(1) <br/> Strongly Disagree',
    '(2)',
    '(3)',
    '(4) <br/> Neither Agree nor Disagree',
    '(5)',
    '(6)',
    '(7) <br/> Strongly Agree',
    'Not Sure'
];
const tmuxLiteCap = simpleMatrixQuestion(`This language's capabilities meet my requirements.`, agreement, 'tmux-lite-capabilities');
const tmuxLiteEase = simpleMatrixQuestion(`This language is easy to use.`, agreement, 'tmux-lite-ease');
// const perceivedEaseOfUse = simpleMatrixQuestion(
//   'language-ease-of-use',
//   ['(1) Very difficult', '(2) Difficult', '(3) Neutral', '(4) Easy', '(5) Very easy'],
//   'How difficult is it to use or write programs in each of these languages?'
// );
const questionLangs = {
    id: 'question-languages',
    type: 'checkbox-question',
    label: 'Which of the following languages that you indicated familiarity with would you feel comfortable answering questions about? (Each set of questions should take about 5-10 minutes)',
    options: (x) => [...fromWhich(x).sort(), 'None of the above'],
    requireResponse: true,
    condition: fromWhichCondition,
    customValidation: elementMutuallyDisallowsOtherOptions('None of the above')
};
const intro = {
    type: 'text-block',
    label: '# JSON-based Domain Specific Languages',
    text: `
<br/>

JSON-based Domain Specific Languages (JSON-DSLs) are a way to write programs in restricted grammars using JSON. Examples of these include the aggregation query language for <a href="https://www.mongodb.com/" target="_blank">MongoDB</a>, the specification language for visualization language <a href="https://vega.github.io/vega-lite/" target="_blank">Vega-Lite</a>, the generative art-bot language <a href="http://www.tracery.io/" target="_blank">Tracery</a>, and a number of others. `,
    id: 'intro',
    requireResponse: false
};
const olderThan18 = {
    type: 'multiple-choice',
    label: 'I am 18 years old or older and I agree to participate in this survey.',
    options: ['Yes', 'No'],
    id: 'older-than-18',
    requireResponse: true
};
const consent = {
    id: 'consent',
    requireResponse: false,
    type: 'text-block',
    label: '# Consent to Participate in a Research Study',
    text: `
__Study Number__: IRB22-0722 
__Study Title__: JSON Domain Specific Languages Survey
__Researcher__: Ravi Chugh (PI), Andrew McNutt

## Description: 
We are researchers at the University of Chicago doing a research study about the usability of JSON-based domain-specific languages. Examples of these include the query language for MongoDB, the specification language for visualization languages Vega-Lite and Vega, the generative art-bot language Tracery, and a number of others. We are interested in developing a better understanding of the usage of JSON-DSLs and how to best improve the design of tools supporting those languages. To facilitate this inquiry we are asking you to complete a survey. This survey does not include questions about personal or sensitive information.  Participation should take about 15-30 minutes. Your participation is voluntary. As long as you have heard of any of these languages (or any other JSON-based domain-specific language) then you qualify for participation in the study.

## Incentives: 
In return for your participation, you will be entered into a lottery to win a $25 Amazon gift certificate. Drawing for this lottery will take place after the conclusion of the survey on October 25th, 2022. The odds of winning will be dependent on the number of participants but will be no better than 1/2 and no worse than 3/25.

## Risks and Benefits: 
The risks to your participation in the survey are those associated with basic computer tasks, including boredom, fatigue, or mild stress. The only benefit to you is the learning experience from participating in a research study. The benefit to society is the contribution to scientific knowledge.

## Confidentiality: 
Ultimately, this research may be published and presented at scientific conferences to improve the community’s knowledge about this form of domain-specific language. Any reports and presentations about the findings from this study will not include your name or any other information that could identify you. If you decide to withdraw from this study, any data already collected will be destroyed.

## Use of Your Research Data:
We will never share the data beyond the University of Chicago research team. However, an analysis of the data may be analyzed and published in scientific conference proceedings or journal articles. The free-text responses you provide to any portion of this survey may be quoted in part or whole in this publication. We will remove any information from the analysis that could identify you before providing the analysis for publication. 

## Voluntary Participation and Right to Refuse or Withdraw:
Participation in this study is voluntary. The decision to participate in this study is entirely up to you. You may refuse to take part in the study at any time without prejudice or penalties to you and will not result in any loss of benefits to which you are otherwise entitled.

Contact Information for Research Questions and Participation

If you have questions or concerns about the study, you can contact the researchers at

Principal Investigator
Ravi Chugh, Associate Professor
John Crerar Library
University of Chicago
5730 S Ellis Ave
Chicago, IL 60637
Email: rchugh@uchicago.edu


Graduate Student
Andrew McNutt, Ph.D. student
John Crerar Library
University of Chicago
5730 S Ellis Ave
Chicago, IL 60637
Email: mcnutt@uchicago.edu

If you have any questions about your rights as a participant in this research, feel you have been harmed, or wish to discuss other study-related concerns with someone who is not part of the research team, you can contact the University of Chicago Social & Behavioral Sciences Institutional Review Board (IRB) Office by phone at (773) 702-2915, or by email at sbs-irb@uchicago.edu.
`
};
//////////////// THANKS QUESTIONS
const thanks = {
    id: 'thanks',
    type: 'text-input',
    size: 'line',
    label: `
# Thanks for participating!

  If you would like to be entered into a raffle to win a $25 Amazon gift certificate then please enter your email below.`,
    requireResponse: false,
    customValidation: emailValidation
};
const followup = {
    id: 'follow-up',
    type: 'text-input',
    size: 'line',
    requireResponse: false,
    label: `Would you be interested in being contacted for a follow-up interview on this topic? If so please provide a contact email. (Entirely optional) `,
    customValidation: emailValidation
};
//////////////// WRAP UP QUESTIONS
// const wrapUpIntro: TextBlock = {
//   id: 'wrap-up',
//   type: 'text-block',
//   requireResponse: false,
//   label: '# Wrap up',
//   text: 'We have now finished the main section of the survey.'
// };
const otherJSONComments = {
    id: 'other-comments',
    type: 'text-input',
    size: 'paragraph',
    requireResponse: false,
    label: `Do you have any other comments on JSON DSLs, tools that support those DSLs, or anything else that you'd like to share with us?`
};
const writtenJSONlanguage = {
    id: 'json-author',
    type: 'text-input',
    size: 'paragraph',
    requireResponse: false,
    label: `Have you written a JSON-based language? If so, what language was it? What guided the design? Why did you select JSON as the syntax for your language?`
};
//////////////// DEMOGRAPHIC QUESTIONS
const demoGraphicsIntro = {
    id: 'demographics-intro',
    type: 'text-block',
    requireResponse: false,
    label: '## Demographics Questions',
    text: 'Please note that while they are all marked as required you can opt out of each of them if you wish.'
};
const yearsProgramming = {
    id: 'years-programming',
    type: 'multiple-choice',
    options: ['Less than 1', '1-2', '3-5', '6-10', '11-20', 'More than 20', 'Prefer not to answer'],
    requireResponse: true,
    label: 'How many years of programming experience do you have?'
};
const gender = {
    id: 'gender',
    type: 'multiple-choice',
    options: ['Woman', 'Man', 'Non-binary', 'Option not listed', 'Prefer not to answer'],
    requireResponse: true,
    label: 'What is your gender?'
};
const age = {
    id: 'age',
    type: 'multiple-choice',
    options: ['18-23', '24-29', '30-35', '36-41', '41-46', '47-51', 'Older than 51', 'Prefer not to answer'],
    requireResponse: true,
    label: 'How old are you?'
};
const techBackground = {
    id: 'tech-background',
    type: 'checkbox-question',
    options: [
        'Computer science / engineering',
        'Information technology',
        'Data Science',
        'Other related field',
        'No',
        'Prefer not to answer'
    ],
    requireResponse: true,
    label: 'Did you finish a degree in (or are you currently majoring in) any of the following fields? Or have you held a job in any of the following fields?',
    customValidation: chainValidations([
        elementMutuallyDisallowsOtherOptions('No'),
        elementMutuallyDisallowsOtherOptions('Prefer not to answer')
    ])
};
const techBackgroundOther = {
    type: 'text-input',
    size: 'line',
    label: 'What field?',
    id: 'tech-background-other',
    condition: (responses) => (responses[`demographics${DIV}tech-background`] || []).find((x) => x === 'Other related field'),
    requireResponse: false
};
/////////////// GENERATED STUFF
const generateLangPageIntro = (lang) => {
    let text = `In this section we will ask a series of questions regarding your opinions about ${lang} and your experiences using it.`;
    if (lang === 'MongoDB') {
        text =
            'In this section we will ask a series of questions regarding your opinions about the MongoDB aggregation language and your experiences using it.';
    }
    return {
        id: `${lang}-intro`,
        type: 'text-block',
        label: `# ${lang} Questions`,
        text,
        requireResponse: false
    };
};
const simpleTextQuestion = (lang) => (label, id) => ({
    type: 'text-input',
    size: 'paragraph',
    label,
    id: `${lang}-${id}`,
    requireResponse: true
});
const generateNextLangPage = (responses, pageStack) => {
    // if they said they aren't familiar with anything bail
    const whichLangs = responses[`basic-questions${DIV}which-languages`] || [];
    if (Array.isArray(whichLangs) && whichLangs[0] === `I'm not familiar with such a language`) {
        return 'finish';
    }
    // if they don't want to answer questions about any of them, offer them some other ones
    const langs = responses[`basic-questions${DIV}question-languages`];
    if (!Array.isArray(langs) || !langs.length || langs[0] === 'None of the above') {
        return 'no-langs-selected';
    }
    // figure out the languages to offer - order they were clicked
    const suffix = '-generated-questions';
    const usedLangs = pageStack.filter((x) => x.endsWith(suffix)).map((x) => x.split(suffix)[0]);
    // if there aren't any more options then call it quits
    const langOptions = langs.filter((x) => !usedLangs.includes(x));
    if (!langOptions.length) {
        return 'demographics';
    }
    const lang = langOptions[0];
    const q = simpleTextQuestion(lang);
    return {
        pageName: `${lang}${suffix}`,
        questions: [
            generateLangPageIntro(lang),
            //  What did you like or dislike about it for those tasks? What situations do you prefer ${lang} over a more traditional programming language for similar tasks?
            q(`__Uses:__ What tasks have used ${lang} for?`, 'which-tasks'),
            // q(`__Preferred Uses:__ `, 'preferred-uses'),
            q(`__Example Usage:__ Can you tell us about something you've made using ${lang}? Why did you write this program and what does it do? If possible, can you share the text of the program or a link to the program in use? `, 'example-usage'),
            q(`__Preferred Uses:__ What do you like about using ${lang}? What situations do you prefer ${lang} over a more traditional programming language for similar tasks?`, 'preferred-uses'),
            q(`__Challenges:__ What aspects of writing or using ${lang} do you find to be the most challenging? Consider your provided example, what was challenging about writing that program?`, 'challenging-tasks'),
            q(`__Environments and tools:__ What environment(s) do you use to create ${lang} programs? Do you use any specialized tools (such as editor extensions or specialized websites) as part of this process? If so what makes these tools useful?`, 'environment'),
            q(`__Debugging__: How do you debug ${lang} programs (again such as your example)? For instance how do you view intermediate values, check correctness, or address other sorts of errors?`, 'debugging'),
            // q(
            //   `__Learning:__ How did you learn or discover new features in ${lang}? In addition to traditional resources like documentation, this might include integrated editor support or other tooling?`,
            //   'how-learned'
            // ),
            q(`__Desired tools:__ What features or tools would make it easier to write, use, debug these programs (such as your example)? What features might make it easier to learn ${lang}?`, 'desired-features')
            // q(
            //   `__Other Comments:__ Do you have any comments on ${lang} you'd like to share with us?`,
            //   'other-features'
            // )
            // q(
            //   `What is your favorite program you've written in ${lang}? What did you like about it? What situations do you prefer it to using a more traditional programming language for similar tasks?`,
            //   'favorite-tasks'
            // ),
            // q(
            //   `Describe the life cycle of a typical program in ${lang} for you. What prompts you to write a program in this language? What environment and tools do you use to create it?`,
            //   'lifecycle'
            // ),
            // q(`Would any particular features or tools have made learning ${lang} easier?`, 'new-learning-tools')
            // What do you do when something isn't working the way you expect it to?
            // q(`Do you have any comments ${lang}?`, 'debugging')
            // q(
            //   `While you are working on a program in ${lang}, how do you view intermediate values?`,
            //   'intermediates'
            // ),
            // q(
            //   `What was the most frustrating time you've had using ${lang}?  What was difficult about it? Did you end up reaching your goal?`,
            //   'hated-tasks'
            // )
            // q(
            //   `What do you do when you encounter something you expect ${lang} to be able to do, but it is not capable of achieving or you can't figure out how to do it?`,
            //   'roadblock'
            // )
            // lang === 'MongoDB' &&
            //   q(`Why do you use Mongo's query language compared something like SQL?`, 'why-this-lang')
            // lang === 'Tracery' &&
            //   q(`Why do you use Tracery rather than creating ?`, 'why-this-lang')
        ],
        allowBack: true,
        nextPage: generateNextLangPage
    };
};
const encounteredButIgnored = {
    id: 'encountered-but-ignored',
    type: 'text-input',
    size: 'paragraph',
    label: 'Have you encountered a JSON-based DSL ([Vega-Lite](https://vega.github.io/vega-lite/), [MongoDB](https://www.mongodb.com/), [Vega](https://vega.github.io/vega/), [Tracery](http://www.tracery.io/), or others), but elected not to use them? If so why did choose not to use it or them?',
    requireResponse: true
};
const futureIncentives = {
    id: 'types-of-tools-in-future',
    type: 'text-input',
    size: 'paragraph',
    label: 'What types of tools, resources, or changes to these systems would cause you to use this one of these systems in the future?',
    requireResponse: true
};
const LanguagesExample = {
    id: 'languages-examples',
    type: 'language-example-widget',
    requireResponse: false,
    label: ''
};
const survey = {
    pages: [
        {
            questions: [consent, olderThan18],
            pageName: 'consent',
            nextPage: (results) => (results[`consent${DIV}older-than-18`] === 'Yes' ? 'basic-questions' : 'finish')
        },
        {
            questions: [
                intro,
                LanguagesExample,
                whichLanguages,
                whichLanguageNotice,
                whichOtherLanguages,
                langFamiliarity,
                langFreq,
                tmuxLiteCap,
                tmuxLiteEase,
                // perceivedEaseOfUse,
                questionLangs
            ],
            pageName: 'basic-questions',
            nextPage: generateNextLangPage
        },
        {
            pageName: 'no-langs-selected',
            questions: [encounteredButIgnored, futureIncentives],
            nextPage: () => 'demographics',
            allowBack: true
        },
        // {
        //   pageName: 'wrap-up-page',
        //   questions: [
        //     // wrapUpIntro,
        //     otherJSONComments,
        //     writtenJSONlanguage
        //   ] as Question[],
        //   nextPage: () => 'demographics',
        //   allowBack: true
        // },
        {
            questions: [
                demoGraphicsIntro,
                yearsProgramming,
                gender,
                age,
                techBackground,
                techBackgroundOther
            ],
            pageName: 'demographics',
            nextPage: () => 'thanks',
            allowBack: true
        },
        {
            questions: [otherJSONComments, writtenJSONlanguage, thanks, followup],
            pageName: 'thanks',
            couldBeFinal: true,
            nextPage: () => 'finish',
            allowBack: true
        }
    ],
    name: 'JSON DSL Survey',
    urlName: 'json-dsl-survey',
    endDate: new Date('2022-10-25T10:30:00.000-06:00'),
    startPage: 'consent'
};
export default survey;
